import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { toast } from 'sonner';

const DISMISS_KEY = 'zazu-notif-prompt-dismissed-at';
const DISMISS_DAYS = 7;

/**
 * Global one-time prompt asking parents and employees to enable push
 * notifications on their device. Shows for anyone signed in when:
 *   - The browser supports notifications
 *   - Notification.permission === 'default' (never asked)
 *   - The user hasn't dismissed it in the last DISMISS_DAYS days
 * When the PWA-install `appinstalled` event fires we re-open the prompt
 * regardless of the dismissal.
 */
export default function NotificationEnrollPrompt() {
  const { user, api } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isSignedIn = !!user;
  const { subscribe } = usePushSubscription(api, isSignedIn);

  useEffect(() => {
    if (!isSignedIn) return undefined;
    if (typeof Notification === 'undefined') return undefined;
    if (Notification.permission !== 'default') return undefined;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return undefined;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || '0');
    const cooldownMs = DISMISS_DAYS * 24 * 60 * 60 * 1000;
    if (dismissedAt && Date.now() - dismissedAt < cooldownMs) return undefined;

    // Delay so we don't shove it in the user's face on page-load
    const timer = setTimeout(() => setOpen(true), 4000);

    const onInstalled = () => setOpen(true);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [isSignedIn]);

  const isParent = user?.user_type === 'parent' || (!user?.is_admin && !user?.is_staff && !user?.intern_role);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await subscribe();
      if (res.subscribed) {
        toast.success('Notifications on!');
        try { await api.post('/push/test'); } catch (err) { void err; }
      } else if (res.reason === 'permission-denied') {
        toast.error('You blocked notifications. Enable them from your browser settings.');
      } else {
        toast.info(`Could not enable: ${res.reason || 'unknown'}`);
      }
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-[9999]"
          data-testid="notif-enroll-prompt"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-emerald-200 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-fredoka font-bold">Stay in the loop</p>
                <p className="text-xs opacity-90">Get updates instantly on this device</p>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="text-white/80 hover:text-white p-1 rounded"
                aria-label="Dismiss"
                data-testid="notif-enroll-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-700 leading-relaxed">
                {isParent
                  ? "We'll ping you when your child earns a badge, when we send you a message, and 5 minutes before any scheduled reminder."
                  : "We'll ping you for @mentions, direct messages, meeting invites, and 5-min meeting reminders."}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={dismiss} data-testid="notif-enroll-later">Later</Button>
                <Button
                  size="sm"
                  onClick={enable}
                  disabled={busy}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="notif-enroll-yes"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  {busy ? 'Enabling…' : 'Yes, enable notifications'}
                </Button>
              </div>
              <a
                href="/notifications/settings"
                className="text-xs text-slate-400 hover:text-emerald-600 text-center block"
                data-testid="notif-enroll-manage"
              >
                Manage which alerts you want →
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
